<template>
    <div v-show="isLoaded" class="other-settings">
        <div class="no-print">
            <p class="common-form-title">{{ $t('settings.autoLogoutTab.title') }}</p>
            <div class="auto-logout">
                <label class="item-label-auto-logout" for="forAT_autoLogout-minutes">{{
                    $t('settings.autoLogoutMinutes.name')
                }}</label>
                <div class="time-field-wrapper">
                    <input
                        id="forAT_autoLogout-minutes"
                        v-model="autoLogout.minutes"
                        v-numeric-only
                        type="text"
                        :class="['input-with-insidelabel', { tooltiperror: $v.autoLogout.minutes.$invalid }]"
                        class="input-number-minutes"
                        @input="$v.autoLogout.minutes.$touch()"
                    />
                    <span v-if="$v.autoLogout.minutes.$invalid" class="tooltiptext">
                        {{
                            errorDisplay($v.autoLogout.minutes, {
                                minValue: MIN_TIMEOUT,
                                maxValue: MAX_TIMEOUT,
                            })
                        }}
                    </span>
                </div>
            </div>
            <p class="common-form-title">{{ $t('settings.otherTab.timeFormat') }}</p>
            <div class="time-format">
                <div class="date">
                    <span>{{ $t('settings.otherTab.date') }}</span>
                    <SelectBox
                        :value="selectedFormat.date"
                        :options="dateList"
                        :search="false"
                        @input="selectFormat('date', $event)"
                    />
                </div>
                <div class="time">
                    <span>{{ $t('settings.otherTab.time') }}</span>
                    <SelectBox
                        :value="selectedFormat.time"
                        :options="timeList"
                        :search="false"
                        @input="selectFormat('time', $event)"
                    />
                </div>
            </div>
            <p class="common-form-title">{{ $t('settings.otherTab.default_recipients') }}</p>
            <div class="default-recipients">
                <div class="multiselect-wrapper">
                    <span v-if="$v.defRecipients.$invalid" class="tooltiptext">
                        {{ errorDisplay($v.defRecipients) }}
                    </span>
                    <Multiselect
                        ref="defRecipientsMultiselect"
                        :value="defRecipients"
                        :placeholder="$t('reports.labels.recipients')"
                        :multiple="true"
                        :taggable="true"
                        :tag-placeholder="$t('reporting.commonLabels.pressEnterToAddEmail')"
                        :options="defRecipients"
                        :max="MAX_EMAILS"
                        :clear-on-select="true"
                        :close-on-select="false"
                        :class="[{ tooltiperror: $v.defRecipients.$invalid }]"
                        @input="updateEmails($event)"
                        @tag="addMail"
                    >
                        <span slot="maxElements">{{
                            $t('reports.labels.maxEmails', { val: MAX_EMAILS })
                        }}</span>
                    </Multiselect>
                </div>
            </div>
        </div>
    </div>
</template>

<script type="text/babel">
import isEqual from 'lodash-es/isEqual';
import Multiselect from 'moxa-vue-multiselect';
import { mapActions, mapState } from 'vuex';
import mappingState from '../common/mappingState';
import { autoLogoutRange, errorDisplay, email } from '../../validators';
import { SUCCESS, MAX_TIMEOUT, MIN_TIMEOUT, MAX_EMAILS, TIME_FORMATS, DATE_FORMATS } from '../../constants';
import { resourceAutoLogout, resourceTimeFormat, resourceDefaultRecipients } from '../../api/resources';
import { ifIsNotCanceled } from '../common/helpers';
import format from 'date-fns/format';
import { getCancelToken } from '../../api';
import commonDirectives from '../common/commonDirectives';
import SelectBox from '../common/SelectBox.vue';

export default {
    components: { SelectBox, Multiselect },

    mixins: [mappingState, commonDirectives],

    constants: {
        MAX_TIMEOUT,
        MIN_TIMEOUT,
        MAX_EMAILS,
    },

    data() {
        return {
            autoLogout: {
                minutes: this.mariTimeout,
            },
            autoLogoutCopy: {
                minutes: '',
            },

            timeFormats: TIME_FORMATS,
            dateFormats: DATE_FORMATS,
            selectedFormat: { ...this.timeFormat },
            selectedFormatCopy: {},
            defRecipients: [''],
            defRecipientsCopy: [],

            cancelToken: getCancelToken(),
        };
    },

    computed: {
        ...mapState([
            'isLoaded',
            'unsavedChanges',
            'mariTimeout',
            'timeFormat',
            'mariCurrentTime',
            'defaultRecipients',
        ]),

        /**
         * Checks whether organization data is valid or not
         * @return {Boolean}
         */
        areDataValid() {
            return !this.$v.autoLogout.minutes.$invalid && !this.$v.defRecipients.$invalid;
        },

        /**
         * Prepare lists for the select-boxes
         */
        dateList() {
            let dates = [];
            DATE_FORMATS.forEach(el => {
                dates.push({ value: el.date, name: format(this.mariCurrentTime, el.date) });
            });
            return dates;
        },

        timeList() {
            let times = [];
            TIME_FORMATS.forEach(el => {
                times.push({ value: el.time, name: format(this.mariCurrentTime, el.time) });
            });
            return times;
        },
    },

    watch: {
        autoLogout: {
            handler() {
                this.setUnsavedChanges(true);
            },
            deep: true,
        },
        selectedFormat: {
            handler() {
                this.setUnsavedChanges(true);
            },
            deep: true,
        },

        defRecipients: {
            handler() {
                this.setUnsavedChanges(true);
            },
            deep: true,
        },

        mariTimeout(minutes) {
            this.autoLogout.minutes = minutes;
            this.autoLogoutCopy = { ...this.autoLogout };
            this.$nextTick(() => {
                this.showLoading(false);
                this.setUnsavedChanges(false);
            });
        },

        defaultRecipients() {
            this.defRecipients = [...this.defaultRecipients];
            this.defRecipientsCopy = [...this.defRecipients];
            this.$nextTick(() => {
                this.showLoading(false);
                this.setUnsavedChanges(false);
            });
        },
    },

    created() {
        this.showLoading(true);
        this.loadData();
        this.setSaveUndoButtons({
            undo: this.undo.bind(this),
            save: this.save.bind(this),
            saveDisabled: () => !this.unsavedChanges || !this.areDataValid,
            wasChanged: () => !this.unsavedChanges,
        });
        this.$nextTick(() => {
            if (this.isLoaded) {
                this.setUnsavedChanges(false);
                this.showLoading(false);
            }
        });
    },

    beforeDestroy() {
        this.cancelToken.cancel();
    },

    validations() {
        return {
            autoLogout: {
                minutes: { autoLogoutRange },
            },
            defRecipients: {
                email,
            },
        };
    },

    methods: {
        ...mapActions({
            setTimeout: 'setTimeout',
            setTimeFormat: 'setTimeFormat',
            setDefaultRecipients: 'setDefaultRecipients',
        }),
        errorDisplay,

        loadData() {
            this.autoLogout.minutes = this.mariTimeout;
            this.autoLogoutCopy = { ...this.autoLogout };

            this.selectedFormat = { ...this.timeFormat };
            this.selectedFormatCopy = { ...this.selectedFormat };

            this.defRecipients = [...this.defaultRecipients];
            this.defRecipientsCopy = [...this.defRecipients];
        },

        selectFormat(item, value) {
            this.selectedFormat[item] = value;
        },

        save() {
            if (this.unsavedChanges && this.areDataValid) {
                this.showLoading(true);
                let saveItems = [];
                if (this.autoLogout.minutes !== this.autoLogoutCopy.minutes) {
                    saveItems.push(this.saveAutoLogout());
                }
                if (!isEqual(this.selectedFormat, this.selectedFormatCopy)) {
                    saveItems.push(this.saveTimeFormat());
                }
                if (this.defRecipients.toString() !== this.defRecipientsCopy.toString()) {
                    saveItems.push(this.saveDefaultRecipients());
                }
                Promise.all(saveItems)
                    .then(() => {
                        this.showLoading(false);
                        this.setUnsavedChanges(false);
                        this.popup({
                            type: SUCCESS,
                            header: this.$t('common.success'),
                            text: this.$t('common.saved'),
                        });
                    })
                    .catch(ifIsNotCanceled());
            }
        },

        /**
         * Sends timeout value into database
         */
        saveAutoLogout() {
            return resourceAutoLogout({
                method: 'post',
                data: this.autoLogout,
                cancelToken: this.cancelToken.token,
            }).then(() => {
                this.setTimeout(this.autoLogout.minutes);
                this.autoLogoutCopy = { ...this.autoLogout };
            });
        },

        /**
         *  Save selected time format
         */
        saveTimeFormat() {
            DATE_FORMATS.forEach(el => {
                if (el.date === this.selectedFormat.date) {
                    this.selectedFormat.php_date = el.php_date;
                    this.selectedFormat.separator = el.separator;
                }
            });
            TIME_FORMATS.forEach(el => {
                if (el.time === this.selectedFormat.time) {
                    this.selectedFormat.php_time = el.php_time;
                }
            });
            return resourceTimeFormat({
                method: 'post',
                data: this.selectedFormat,
                cancelToken: this.cancelToken.token,
            }).then(() => {
                this.setTimeFormat(Object.assign({}, this.selectedFormat));
                this.$el.dispatchEvent(new Event('setTimeFormatEvent', { bubbles: true }));
                this.selectedFormatCopy = { ...this.selectedFormat };
            });
        },

        saveDefaultRecipients() {
            return resourceDefaultRecipients({
                method: 'post',
                data: { emails: this.defRecipients },
                cancelToken: this.cancelToken.token,
            }).then(() => {
                this.defRecipientsCopy = [...this.defRecipients];
                this.setDefaultRecipients(this.defRecipientsCopy);
            });
        },

        updateEmails(event) {
            this.defRecipients = event;
        },

        addMail(newMail) {
            if (newMail !== '') {
                const clippedEmail = newMail.trim();
                this.updateEmails([...this.defRecipients, clippedEmail]);
            }
        },
        /**
         * Reset unsaved changes
         */
        undo() {
            this.autoLogout.minutes = this.autoLogoutCopy.minutes;
            this.selectedFormat = { ...this.selectedFormatCopy };
            this.defRecipients = [...this.defRecipientsCopy];
            this.$nextTick(() => {
                this.setUnsavedChanges(false);
            });
        },
    },
};
</script>
